/**
 * Relay tolls on the player's jumps (M5 spec §5.4, §14.4, §18; seams S-M3-10, S-M2-14; plan C15).
 *
 * M3's travel subsystem asks the installed `TollPolicy` right before a relay transit starts (`REV.transitStart`):
 * a jump into a system whose holder charges tolls (the galaxy view's `charter.toll`, the medium-hull toll, scaled
 * to the player's hull class from claims.json) shows "Toll 150 (Tethys claim)", pays through `CareerPort.spend`
 * and reports `tollPaid { relayId, amount }`; without the funds the ship jumps anyway and `tollEvaded { relayId }`
 * goes to the core (a crime). An unclaimed or toll-free system costs nothing and reports nothing.
 *
 * The jump is never blocked or delayed: spending is synchronous and the report is fire and forget. Rare event
 * (one relay jump): allocates freely.
 */
import { z } from 'zod'
import claimsJson from '../data/strategic/claims.json'
import conflictJson from '../data/charter-conflict.json'
import { logger } from '../core/logger'
import { loadFactionsConfig, type FactionsConfig } from '../galaxy/layout/config'
import type { CareerPort } from '../reach/ports'
import type { CommandResult, FactionId, GalaxyEconomyView, StrategicCommand } from '../strategic/types'
import { HULL_CLASS_NAMES, PLAYER_FACTION_ID } from '../strategic/types'

const tollsConfigSchema = z
  .object({
    playerHullClass: z.enum(HULL_CLASS_NAMES),
  })
  .strict()
const tollCreditsSchema = z.object({ light: z.number().nonnegative(), medium: z.number().nonnegative(), large: z.number().nonnegative() })

/** The player's hull class (charter-conflict.json `tolls`). */
export const TOLLS_CONFIG = tollsConfigSchema.parse(conflictJson.tolls)
/** Toll credits per hull class (claims.json `toll.credits`). */
const TOLL_CREDITS = tollCreditsSchema.parse(claimsJson.toll.credits)

/** Where a destination's claim and toll come from (the galaxy view's per-system Charter summary). */
export interface TollSource {
  /** The holder and the medium-hull toll of `systemId`, or null when unknown or unclaimed. */
  claimOf(systemId: string): { holder: FactionId; toll: number } | null
}

/** A toll to pay for one jump. */
export interface TollQuote {
  amount: number
  holder: FactionId
  /** "Toll 150 (Tethys claim)". */
  label: string
}

export type TollOutcome = 'free' | 'paid' | 'evaded'

export interface TollDeps {
  readonly source: TollSource
  readonly career: Pick<CareerPort, 'spend'>
  command(cmd: StrategicCommand): Promise<CommandResult>
  /** Shows the toll line (a HUD toast); optional. */
  notify?(text: string): void
  readonly factions?: FactionsConfig
}

/** The short name of a faction ("Tethys"), or its id when M3's factions do not list it. */
export function factionShort(id: FactionId, factions: FactionsConfig = defaultFactions()): string {
  return Object.hasOwn(factions.factions, id) ? factions.factions[id as keyof FactionsConfig['factions']].short : id
}

let cachedFactions: FactionsConfig | null = null
function defaultFactions(): FactionsConfig {
  cachedFactions ??= loadFactionsConfig()
  return cachedFactions
}

/** The toll for a hull of the player's class given the medium-hull toll: `medium × credits[class] / credits.medium`. */
export function playerToll(mediumToll: number, hullClass: (typeof HULL_CLASS_NAMES)[number] = TOLLS_CONFIG.playerHullClass): number {
  if (!(mediumToll > 0)) return 0
  const medium = TOLL_CREDITS.medium
  if (!(medium > 0)) return 0
  return Math.round((mediumToll * TOLL_CREDITS[hullClass]) / medium)
}

/** The toll a jump into `destSystemId` costs the player, or null when it is free. */
export function quoteToll(source: TollSource, destSystemId: string, factions?: FactionsConfig): TollQuote | null {
  const claim = source.claimOf(destSystemId)
  if (claim === null) return null
  const amount = playerToll(claim.toll)
  if (!(amount > 0)) return null
  return { amount, holder: claim.holder, label: `Toll ${amount.toLocaleString('en-US')} (${factionShort(claim.holder, factions)} claim)` }
}

function logReportError(err: unknown): void {
  logger.warn('charter tolls: the toll report failed', err)
}

/**
 * The toll check before a relay transit through `relayId` into `destSystemId` (see the file header). Returns what
 * happened; never blocks the jump.
 */
export function chargeToll(deps: TollDeps, relayId: string, destSystemId: string): TollOutcome {
  const q = quoteToll(deps.source, destSystemId, deps.factions)
  if (q === null) return 'free'
  if (deps.career.spend(q.amount, 'toll')) {
    deps.notify?.(q.label)
    deps.command({ type: 'tollPaid', relayId, amount: q.amount }).catch(logReportError)
    return 'paid'
  }
  deps.notify?.(`${q.label}: not paid`)
  deps.command({ type: 'tollEvaded', relayId }).catch(logReportError)
  return 'evaded'
}

/** A `TollSource` over the galaxy view's Charter summaries (`GalaxySystemEconomy.charter`). */
export function galaxyTollSource(view: () => GalaxyEconomyView | null): TollSource {
  return {
    claimOf(systemId: string): { holder: FactionId; toll: number } | null {
      const v = view()
      if (v === null) return null
      for (const s of v.systems) {
        if (s.id !== systemId) continue
        const c = s.charter
        // The corporation's toll is always 0 (D-424, M5 spec §27.6): its claims charge nothing.
        return c === undefined || c.holder === null || c.holder === PLAYER_FACTION_ID ? null : { holder: c.holder, toll: c.toll }
      }
      return null
    },
  }
}

/** The check M3's travel subsystem runs before `transitStart` (S-M3-10). */
export type TollPolicy = (relayId: string, destSystemId: string, career: Pick<CareerPort, 'spend'>) => TollOutcome

let installed: TollPolicy | null = null

/** Installs the policy (or with null removes it); returns a function restoring the previous one. */
export function installTollPolicy(policy: TollPolicy | null): () => void {
  const previous = installed
  installed = policy
  return () => {
    installed = previous
  }
}

/** The installed policy, or null (no strategic economy: jumps are free). */
export function tollPolicy(): TollPolicy | null {
  return installed
}

/** What the live policy reads of the strategic client. */
export interface TollClient {
  readonly galaxyView: GalaxyEconomyView | null
  onGalaxyView(fn: (view: GalaxyEconomyView) => void): () => void
  command(cmd: StrategicCommand): Promise<CommandResult>
}

/**
 * The live policy over the strategic client: it subscribes to the galaxy view (so the per-system tolls stay
 * fresh) and charges through the career the travel subsystem passes. Returns the policy and its unsubscribe.
 */
export function createTollPolicy(client: TollClient, notify?: (text: string) => void): { policy: TollPolicy; dispose: () => void } {
  let latest: GalaxyEconomyView | null = client.galaxyView
  const off = client.onGalaxyView((v) => {
    latest = v
  })
  const source = galaxyTollSource(() => latest ?? client.galaxyView)
  const policy: TollPolicy = (relayId, destSystemId, career) =>
    chargeToll({ source, career, command: (cmd) => client.command(cmd), notify }, relayId, destSystemId)
  return { policy, dispose: off }
}

let notifier: ((text: string) => void) | null = null

/** Sets where the live policy shows its toll line (C18's HUD toasts); null logs it only. Returns the restore. */
export function setTollNotifier(fn: ((text: string) => void) | null): () => void {
  const previous = notifier
  notifier = fn
  return () => {
    notifier = previous
  }
}

function notifyToll(text: string): void {
  logger.info(`charter tolls: ${text}`)
  notifier?.(text)
}

/**
 * Installs the live policy over the running strategic client (the Charter conflict runtime does, on binding the
 * client); returns the uninstall. The toll line goes to the logger and to the notifier (C18's toasts).
 */
export function installLiveTollPolicy(client: TollClient): () => void {
  const made = createTollPolicy(client, notifyToll)
  const restore = installTollPolicy(made.policy)
  return () => {
    restore()
    made.dispose()
  }
}

/**
 * The hook M3's travel subsystem calls right before `transitStart` (S-M3-10): the installed policy; free (and
 * nothing reported) without one (no strategic economy).
 */
export function tollBeforeTransit(career: Pick<CareerPort, 'spend'>, relayId: string, destSystemId: string): TollOutcome {
  return installed === null ? 'free' : installed(relayId, destSystemId, career)
}
